import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from './api';

export interface FieldDescriptor {
  field: string;
  label: string;
}

export interface UploadResponse {
  upload_id: string;
  filename: string;
  columns: string[];
  total_rows: number;
  sample_rows: Array<Record<string, unknown>>;
  auto_detected_mapping: Record<string, string>;
  supported_fields: FieldDescriptor[];
}

export interface CandidatePart {
  part_id: number;
  part_number: string;
  mfg_pn: string | null;
  description: string | null;
  value: string | null;
  package: string | null;
}

export type ResolveStatus = 'matched' | 'ambiguous' | 'unmatched';

export interface ResolvedRow {
  row_indices: number[];
  reference_designators: string[];
  quantity: number;
  raw_part_number: string | null;
  raw_mfg_pn: string | null;
  raw_value: string | null;
  raw_package: string | null;
  raw_manufacturer: string | null;
  raw_description: string | null;
  status: ResolveStatus;
  matched_part: CandidatePart | null;
  candidates: CandidatePart[];
  message: string | null;
}

export interface ResolveResponse {
  upload_id: string;
  rows: ResolvedRow[];
  matched_count: number;
  ambiguous_count: number;
  unmatched_count: number;
}

export interface CommitDecision {
  row_indices: number[];
  part_id: number | null;
  quantity: number;
  reference_designators: string[];
  notes?: string | null;
}

export interface CommitOutcome {
  row_indices: number[];
  action: 'added' | 'skipped' | 'errored';
  part_id: number | null;
  part_number: string | null;
  quantity: number;
  reference_designators: string[];
  message: string | null;
}

export interface CommitResponse {
  dry_run: boolean;
  added: number;
  skipped: number;
  errored: number;
  outcomes: CommitOutcome[];
}

function urlBase(orgSlug: string, bomId: number, revisionId: number) {
  return `/api/orgs/${orgSlug}/boms/${bomId}/revisions/${revisionId}/import`;
}

async function uploadBom(
  orgSlug: string,
  bomId: number,
  revisionId: number,
  file: File,
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${urlBase(orgSlug, bomId, revisionId)}/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const text = await response.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!response.ok) {
    const detail =
      typeof body === 'object' && body !== null && 'detail' in body
        ? String((body as { detail: unknown }).detail)
        : `HTTP ${response.status}`;
    throw new ApiError(detail, response.status, body);
  }
  return body as UploadResponse;
}

export function useUploadBom(
  orgSlug: string | undefined,
  bomId: number | null,
  revisionId: number | null,
) {
  return useMutation({
    mutationFn: (file: File) => {
      if (!orgSlug || !bomId || !revisionId) throw new Error('missing context');
      return uploadBom(orgSlug, bomId, revisionId, file);
    },
  });
}

export function useResolveBom(
  orgSlug: string | undefined,
  bomId: number | null,
  revisionId: number | null,
) {
  return useMutation({
    mutationFn: (input: {
      upload_id: string;
      column_mapping: Record<string, string>;
    }) => {
      if (!orgSlug || !bomId || !revisionId) throw new Error('missing context');
      return api.postJson<ResolveResponse>(
        `${urlBase(orgSlug, bomId, revisionId)}/resolve`,
        input,
      );
    },
  });
}

export function useCommitBomImport(
  orgSlug: string | undefined,
  bomId: number | null,
  revisionId: number | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      upload_id: string;
      decisions: CommitDecision[];
      dry_run: boolean;
    }) => {
      if (!orgSlug || !bomId || !revisionId) throw new Error('missing context');
      return api.postJson<CommitResponse>(
        `${urlBase(orgSlug, bomId, revisionId)}/commit`,
        input,
      );
    },
    onSuccess: (data) => {
      if (data.dry_run) return;
      // Real import landed — refresh the revision detail and the
      // line-count summary on the BOM list page.
      qc.invalidateQueries({ queryKey: ['bom-revision', orgSlug, bomId, revisionId] });
      qc.invalidateQueries({ queryKey: ['bom-revisions', orgSlug, bomId] });
      qc.invalidateQueries({ queryKey: ['bom', orgSlug, bomId] });
      qc.invalidateQueries({ queryKey: ['boms', orgSlug] });
    },
  });
}
